"use client";

import { usePathname, useRouter } from "next/navigation";
import { useEffect, type ReactNode } from "react";

import { useSession } from "@/lib/auth-client";
import { shouldEnforcePasswordChangeRedirectClient } from "@/lib/password-change-enforcement";
import { useAuth } from "@/providers/auth";

const ACCOUNT = "/account";
const WELCOME = "/welcome";

function isAuthPublicPath(pathname: string): boolean {
	return (
		pathname.startsWith("/login") ||
		pathname.startsWith("/sign-in") ||
		pathname.startsWith("/forgot") ||
		pathname.startsWith("/reset-password") ||
		pathname.startsWith("/register")
	);
}

/**
 * - `initialPasswordPending`: checkout signup must set a password on `/welcome` (always enforced).
 * - `mustChangePassword`: admin/dev forcing — keep on account page until changed.
 * - Skipped while admin is impersonating so staff can see the real user experience.
 */
export function ForcePasswordChangeGate({ children }: { children: ReactNode }) {
	const pathname = usePathname() ?? "";
	const router = useRouter();
	const { user, isLoading, isLoggedIn, isSessionResolving } = useAuth();
	const { data: session } = useSession();
	const isImpersonating = Boolean(
		(session as { session?: { impersonatedBy?: string | null } } | null | undefined)?.session
			?.impersonatedBy,
	);
	const enforceMustChange = shouldEnforcePasswordChangeRedirectClient();
	const mustChange = Boolean(user?.mustChangePassword);
	const initialPending = Boolean(user?.initialPasswordPending);
	const isAccountPath = pathname === ACCOUNT || pathname.startsWith(`${ACCOUNT}/`);
	const isWelcomePath = pathname === WELCOME || pathname.startsWith(`${WELCOME}/`);
	const shouldBlockForResolution =
		!isImpersonating &&
		!isWelcomePath &&
		!isAccountPath &&
		(isSessionResolving || isLoading) &&
		(initialPending || (enforceMustChange && mustChange));

	useEffect(() => {
		if (isImpersonating || shouldBlockForResolution || !isLoggedIn) {
			return;
		}

		if (initialPending && !isWelcomePath && !isAuthPublicPath(pathname)) {
			router.replace(WELCOME);
			return;
		}

		if (!enforceMustChange || !mustChange || isAccountPath || isAuthPublicPath(pathname)) {
			return;
		}

		router.replace(`${ACCOUNT}#password`);
	}, [
		enforceMustChange,
		initialPending,
		isAccountPath,
		isImpersonating,
		isLoggedIn,
		isWelcomePath,
		mustChange,
		pathname,
		router,
		shouldBlockForResolution,
	]);

	if (isImpersonating) {
		return <>{children}</>;
	}

	if (shouldBlockForResolution) {
		return (
			<div className="flex min-h-[calc(100svh-200px)] items-center justify-center px-4">
				<div className="h-8 w-48 animate-pulse rounded bg-quaternary/50" />
			</div>
		);
	}

	if (initialPending && !isWelcomePath && isLoggedIn) {
		return (
			<div className="flex min-h-[calc(100svh-200px)] items-center justify-center px-4">
				<p className="text-sm text-tertiary">Redirecting to finish account setup…</p>
			</div>
		);
	}

	if (enforceMustChange && !isAccountPath && isLoggedIn && mustChange) {
		return (
			<div className="flex min-h-[calc(100svh-200px)] items-center justify-center px-4">
				<p className="text-sm text-tertiary">Redirecting to account security…</p>
			</div>
		);
	}

	return <>{children}</>;
}
